import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiDelete } from '@/utils/api';
import { NotificationSettings } from '@/components/NotificationSettings';

function SecurityBadge({ icon, title, description }) {
  return (
    <article className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow" aria-label={title}>
      <div className="text-3xl mb-3" aria-hidden="true">{icon}</div>
      <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </article>
  );
}

function DataItem({ icon, text, isGood }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${isGood ? 'bg-green-50' : 'bg-red-50'}`} role="listitem">
      <span className="text-xl" aria-hidden="true">{icon}</span>
      <span className={`font-medium ${isGood ? 'text-green-800' : 'text-red-800'}`}>{text}</span>
    </div>
  );
}

function Security() {
  const [securityInfo, setSecurityInfo] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [twoFactorSettings, setTwoFactorSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling2FA, setToggling2FA] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // TOTP states
  const [showTOTPSetup, setShowTOTPSetup] = useState(false);
  const [totpSetupData, setTotpSetupData] = useState(null);
  const [totpVerifyCode, setTotpVerifyCode] = useState('');
  const [settingUpTOTP, setSettingUpTOTP] = useState(false);
  const [verifyingTOTP, setVerifyingTOTP] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get public security info
        const infoRes = await apiGet('/security/info');
        if (infoRes.ok) {
          const data = await infoRes.json();
          setSecurityInfo(data);
        }

        // Check if user is authenticated
        const meRes = await apiGet('/auth/me');
        if (meRes.ok) {
          setIsAuthenticated(true);
          
          // Get sessions
          const sessionsRes = await apiGet('/security/sessions');
          if (sessionsRes.ok) {
            const data = await sessionsRes.json();
            setSessions(data);
          }

          // Get 2FA settings
          const tfaRes = await apiGet('/auth/2fa/settings');
          if (tfaRes.ok) {
            const data = await tfaRes.json();
            setTwoFactorSettings(data);
          }

          // Get audit log
          const auditRes = await apiGet('/security/audit-log?limit=10');
          if (auditRes.ok) {
            const data = await auditRes.json();
            setAuditLog(data);
          }
        }
      } catch (error) {
        console.error('Error loading security data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleDownloadData = async () => {
    setDownloading(true);
    try {
      const response = await apiGet('/auth/export-my-data');
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `guapgoals_data_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to export data. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading data:', error);
      alert('Failed to download data. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleToggle2FA = async () => {
    setToggling2FA(true);
    try {
      const endpoint = twoFactorSettings?.enabled ? '/auth/2fa/disable' : '/auth/2fa/enable';
      const response = await apiPost(endpoint, {});
      if (response.ok) {
        const data = await response.json();
        setTwoFactorSettings({ ...twoFactorSettings, enabled: data.enabled });
      }
    } catch (error) {
      console.error('Error toggling 2FA:', error);
      alert('Failed to update 2FA settings. Please try again.');
    } finally {
      setToggling2FA(false);
    }
  };

  const handleClearDevices = async () => {
    if (!window.confirm('This will require 2FA verification on your next login from any device. Continue?')) {
      return;
    }
    try {
      const response = await apiPost('/auth/2fa/clear-devices', {});
      if (response.ok) {
        setTwoFactorSettings({ ...twoFactorSettings, verified_devices_count: 0 });
        alert('All verified devices cleared.');
      }
    } catch (error) {
      console.error('Error clearing devices:', error);
    }
  };

  const handleLogoutAll = async () => {
    if (!window.confirm('This will log you out from all devices including this one. Continue?')) {
      return;
    }
    
    setLoggingOut(true);
    try {
      const response = await apiPost('/security/logout-all', {});
      if (response.ok) {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Error logging out:', error);
      alert('Failed to logout. Please try again.');
    } finally {
      setLoggingOut(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const response = await apiDelete('/security/delete-account?confirmation=DELETE_MY_ACCOUNT');
      if (response.ok) {
        alert('Your account has been permanently deleted.');
        window.location.href = '/login';
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to delete account.');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSetupTOTP = async () => {
    setSettingUpTOTP(true);
    try {
      const response = await apiPost('/auth/2fa/setup-totp', {});
      if (response.ok) {
        const data = await response.json();
        setTotpSetupData(data);
      } else {
        alert('Failed to generate TOTP setup');
      }
    } catch (error) {
      console.error('Error setting up TOTP:', error);
      alert('Failed to set up TOTP');
    } finally {
      setSettingUpTOTP(false);
    }
  };

  const handleVerifyTOTP = async () => {
    setVerifyingTOTP(true);
    try {
      const response = await apiPost('/auth/2fa/verify-totp-setup', { code: totpVerifyCode });
      if (response.ok) {
        setTwoFactorSettings({ ...twoFactorSettings, enabled: true, method: 'totp' });
        setShowTOTPSetup(false);
        setTotpSetupData(null);
        setTotpVerifyCode('');
        alert('Authenticator app 2FA enabled successfully!');
      } else {
        const data = await response.json();
        alert(data.detail || 'Invalid code');
      }
    } catch (error) {
      console.error('Error verifying TOTP:', error);
      alert('Failed to verify code');
    } finally {
      setVerifyingTOTP(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading security info...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="max-w-4xl mx-auto space-y-8" data-testid="security-page" role="main" aria-label="Security and Privacy">
      {/* Header */}
      <header className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4" aria-hidden="true">
          <span className="text-3xl">🔒</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Security & Privacy</h1>
        <p className="text-gray-600 mt-2">Your data is protected. Here&apos;s how.</p>
      </header>

      {/* Security Badges */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-4" aria-label="Security features">
        <SecurityBadge
          icon="🔐"
          title="Google OAuth"
          description="We never see or store your password. Authentication is handled securely by Google."
        />
        <SecurityBadge
          icon="🛡️"
          title="Encrypted Connection"
          description="All data is transmitted over HTTPS with TLS encryption."
        />
        <SecurityBadge
          icon="⏱️"
          title="Auto-Logout"
          description="Sessions automatically expire after 30 minutes of inactivity."
        />
        <SecurityBadge
          icon="🔒"
          title="Hacker-Proof Security"
          description="Your sensitive data is encrypted at rest. Even if hackers got in, they'd get nothing useful."
        />
      </section>

      {/* Two-Factor Authentication */}
      {isAuthenticated && (
        <section className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" aria-label="Two-Factor Authentication">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="text-3xl" aria-hidden="true">📱</div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Two-Factor Authentication</h2>
                <p className="text-sm text-gray-600">
                  Add an extra layer of security to your account
                </p>
              </div>
            </div>
            {twoFactorSettings?.enabled && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                twoFactorSettings.method === 'totp' 
                  ? 'bg-purple-100 text-purple-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {twoFactorSettings.method === 'totp' ? '🔐 Authenticator App' : '📧 Email Code'}
              </span>
            )}
          </div>
          
          {!twoFactorSettings?.enabled ? (
            // Not enabled - show setup options
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800">
                  <strong>Recommended:</strong> Enable 2FA to protect your account from unauthorized access.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Email 2FA Option */}
                <button
                  onClick={handleToggle2FA}
                  disabled={toggling2FA}
                  className="p-4 border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all text-left"
                  data-testid="enable-email-2fa-btn"
                >
                  <div className="text-2xl mb-2">📧</div>
                  <h3 className="font-bold text-gray-900">Email Code</h3>
                  <p className="text-sm text-gray-600">Receive a code via email each time you log in</p>
                </button>
                
                {/* TOTP 2FA Option */}
                <button
                  onClick={() => setShowTOTPSetup(true)}
                  className="p-4 border-2 border-purple-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all text-left"
                  data-testid="enable-totp-2fa-btn"
                >
                  <div className="text-2xl mb-2">🔐</div>
                  <h3 className="font-bold text-gray-900">Authenticator App</h3>
                  <p className="text-sm text-gray-600">Use Google Authenticator, Authy, or similar</p>
                </button>
              </div>
            </div>
          ) : (
            // Enabled - show status and options
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span className="text-green-800 font-medium">
                    2FA is enabled using {twoFactorSettings.method === 'totp' ? 'Authenticator App' : 'Email'}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">{twoFactorSettings.verified_devices_count}</span> trusted device(s)
                  </p>
                  <p className="text-xs text-gray-500">Trusted devices skip 2FA verification</p>
                </div>
                {twoFactorSettings.verified_devices_count > 0 && (
                  <button
                    onClick={handleClearDevices}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Clear all
                  </button>
                )}
              </div>
              
              {twoFactorSettings.method === 'totp' && twoFactorSettings.backup_codes_count > 0 && (
                <div className="p-4 bg-purple-50 rounded-xl">
                  <p className="text-sm text-purple-800">
                    <span className="font-medium">{twoFactorSettings.backup_codes_count}</span> backup codes remaining
                  </p>
                </div>
              )}
              
              <button
                onClick={handleToggle2FA}
                disabled={toggling2FA}
                className="text-sm text-red-600 hover:text-red-800"
                data-testid="disable-2fa-btn"
              >
                {toggling2FA ? 'Disabling...' : 'Disable 2FA'}
              </button>
            </div>
          )}
        </section>
      )}
      
      {/* TOTP Setup Modal */}
      {showTOTPSetup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
            <div className="text-center">
              <div className="text-4xl mb-2">🔐</div>
              <h2 className="text-xl font-bold text-gray-900">Set Up Authenticator App</h2>
            </div>
            
            {!totpSetupData ? (
              <div className="space-y-4">
                <p className="text-gray-600 text-center">
                  Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
                <button
                  onClick={handleSetupTOTP}
                  disabled={settingUpTOTP}
                  className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 disabled:opacity-50"
                >
                  {settingUpTOTP ? 'Generating...' : 'Generate QR Code'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* QR Code */}
                <div className="flex justify-center">
                  <img src={totpSetupData.qr_code} alt="TOTP QR Code" className="w-48 h-48" />
                </div>
                
                {/* Manual entry */}
                <div className="p-3 bg-gray-100 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Manual entry key:</p>
                  <p className="font-mono text-sm break-all">{totpSetupData.secret}</p>
                </div>
                
                {/* Backup codes */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm font-medium text-amber-800 mb-2">⚠️ Save your backup codes:</p>
                  <div className="grid grid-cols-2 gap-1 font-mono text-sm">
                    {totpSetupData.backup_codes.map((code, i) => (
                      <span key={i} className="text-amber-900">{code}</span>
                    ))}
                  </div>
                </div>
                
                {/* Verification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enter code from your app to verify:
                  </label>
                  <input
                    type="text"
                    value={totpVerifyCode}
                    onChange={(e) => setTotpVerifyCode(e.target.value)}
                    placeholder="000000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl text-center text-2xl font-mono tracking-widest"
                    maxLength={6}
                  />
                </div>
                
                <button
                  onClick={handleVerifyTOTP}
                  disabled={verifyingTOTP || totpVerifyCode.length !== 6}
                  className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 disabled:opacity-50"
                >
                  {verifyingTOTP ? 'Verifying...' : 'Verify & Enable'}
                </button>
              </div>
            )}
            
            <button
              onClick={() => {
                setShowTOTPSetup(false);
                setTotpSetupData(null);
                setTotpVerifyCode('');
              }}
              className="w-full py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* What We Store vs Don't Store */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="text-green-600">✓</span> What We Store
          </h2>
          <div className="space-y-2">
            {securityInfo?.data_practices?.what_we_store?.map((item, i) => (
              <DataItem key={i} icon="📝" text={item} isGood={true} />
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="text-red-600">✗</span> What We NEVER Store
          </h2>
          <div className="space-y-2">
            {securityInfo?.data_practices?.what_we_dont_store?.map((item, i) => (
              <DataItem key={i} icon="🚫" text={item} isGood={false} />
            ))}
          </div>
        </div>
      </div>

      {/* Your Data, Your Way Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white text-center">
        <h2 className="text-xl font-bold mb-2">🏦 Your Data, Your Way</h2>
        <p className="text-blue-100">
          Enter your budget manually, or use Plaid for auto-sync. Either way, 
          we use bank-level encryption and never store your login credentials. You're always in control.
        </p>
      </div>

      {/* Data Controls - Only for authenticated users */}
      {isAuthenticated && (
        <section className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" aria-label="Data Controls">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Your Data Controls</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={handleDownloadData}
              disabled={downloading}
              className="p-4 bg-blue-50 rounded-xl text-left hover:bg-blue-100 transition-colors border border-blue-200 disabled:opacity-50"
            >
              <div className="text-2xl mb-2">📥</div>
              <h3 className="font-bold text-gray-900">Download My Data</h3>
              <p className="text-sm text-gray-600">
                {downloading ? 'Downloading...' : 'Get a copy of all your data'}
              </p>
            </button>

            <button
              onClick={handleLogoutAll}
              disabled={loggingOut}
              className="p-4 bg-amber-50 rounded-xl text-left hover:bg-amber-100 transition-colors border border-amber-200 disabled:opacity-50"
            >
              <div className="text-2xl mb-2">🚪</div>
              <h3 className="font-bold text-gray-900">Logout All Devices</h3>
              <p className="text-sm text-gray-600">
                {loggingOut ? 'Logging out...' : `${sessions?.active_sessions || 0} active session(s)`}
              </p>
            </button>

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-4 bg-red-50 rounded-xl text-left hover:bg-red-100 transition-colors border border-red-200"
            >
              <div className="text-2xl mb-2">🗑️</div>
              <h3 className="font-bold text-gray-900">Delete Account</h3>
              <p className="text-sm text-gray-600">Permanently remove all data</p>
            </button>
          </div>
        </section>
      )}

      {/* Session Info */}
      {isAuthenticated && sessions && (
        <section className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" aria-label="Active Sessions">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-900">📱 Active Sessions</h2>
            <span className="text-sm text-gray-500">{sessions.active_sessions} session(s)</span>
          </div>
          
          <div className="space-y-3">
            {sessions.sessions?.map((session, i) => (
              <div key={i} className={`p-4 rounded-xl border ${session.is_current ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">
                        {session.user_agent || 'Unknown Device'}
                      </p>
                      {session.is_current && (
                        <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                          This device
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      IP: {session.ip_address || 'Unknown'} • Last active: {formatDate(session.last_activity)}
                    </p>
                  </div>
                  {!session.is_current && (
                    <button
                      onClick={async () => {
                        if (window.confirm('Log out this device?')) {
                          try {
                            const response = await apiDelete(`/security/sessions/${session.device_fingerprint}`);
                            if (response.ok) {
                              // Reload sessions
                              const sessionsRes = await apiGet('/security/sessions');
                              if (sessionsRes.ok) {
                                const data = await sessionsRes.json();
                                setSessions(data);
                              }
                            }
                          } catch (error) {
                            console.error('Error logging out session:', error);
                          }
                        }
                      }}
                      className="text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Log out
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {sessions.active_sessions > 1 && (
            <button
              onClick={handleLogoutAll}
              disabled={loggingOut}
              className="mt-4 w-full py-2 text-red-600 hover:text-red-800 font-medium text-sm border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
            >
              {loggingOut ? 'Logging out...' : 'Log out all other devices'}
            </button>
          )}
        </section>
      )}

      {/* Notification Settings */}
      <NotificationSettings />

      {/* Audit Log */}
      {isAuthenticated && auditLog?.audit_entries?.length > 0 && (
        <section className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" aria-label="Recent Activity">
          <h2 className="text-lg font-bold text-gray-900 mb-4">📋 Recent Activity</h2>
          
          <div className="space-y-2">
            {auditLog.audit_entries.map((entry, i) => (
              <div key={i} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">{entry.action.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-gray-500">{formatDate(entry.timestamp)}</p>
                </div>
                {entry.ip_address && (
                  <span className="text-xs text-gray-400">IP: {entry.ip_address.substring(0, 12)}...</span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Third Party Info */}
      <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🤝 Third-Party Sharing</h2>
        <div className="flex items-center gap-4">
          <div className="text-4xl">🚫</div>
          <div>
            <p className="font-bold text-gray-900">We never sell or share your data</p>
            <p className="text-sm text-gray-600">
              Your financial information stays between you and GuapGoals. Period.
            </p>
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="text-center space-y-4">
        <p className="text-gray-600">
          <Link to="/app/contact" className="text-blue-600 hover:text-blue-800 font-medium underline">
            Questions about security?
          </Link>
        </p>
        <div className="flex justify-center gap-4">
          <Link to="/app/dashboard" className="text-blue-600 hover:text-blue-800 font-medium">
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="text-center mb-4">
              <div className="text-5xl mb-4">⚠️</div>
              <h3 className="text-xl font-bold text-gray-900">Delete Your Account?</h3>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-amber-800 text-sm font-medium mb-2">
                🕐 You have 24 hours to change your mind!
              </p>
              <p className="text-amber-700 text-sm">
                Log back in within 24 hours to recover your account. After that, everything's gone for good.
              </p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-red-800 text-sm">
                <strong>After 24 hours, this data will be permanently deleted:</strong>
              </p>
              <ul className="text-red-700 text-sm mt-2 space-y-1">
                <li>• All bills and income sources</li>
                <li>• All savings goals and allocations</li>
                <li>• All budget history</li>
                <li>• Your account settings</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Processing...' : 'Yes, Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Security;

